import { useEffect, useRef, useState, type FormEvent } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useRepoContext } from '../contexts/RepoContext';

interface Hit {
    repoId: string;
    path: string;
    title?: string;
    snippet: string;
    score: number;
    openable: boolean;
}

interface SearchResponse {
    hits: Hit[];
    answer: string | null;
}

interface Props {
    onClose: () => void;
    onOpenDoc: (repoId: string, path: string) => void;
}

export function AISearchModal({ onClose, onOpenDoc }: Props) {
    const { allowedRepos, effectiveRepoId } = useRepoContext();
    const [scope, setScope] = useState<string>(effectiveRepoId ?? ''); // '' = all accessible repos
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SearchResponse | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    async function submit(e: FormEvent) {
        e.preventDefault();
        if (!q.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const body: { query: string; maxResults: number; synthesize: true; repoId?: string } = {
                query: q,
                maxResults: 8,
                synthesize: true
            };
            if (scope) body.repoId = scope;
            const r = await apiCall<SearchResponse>('/ai/search', { method: 'POST', body });
            setResult(r);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content modal-content--ai" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2><span aria-hidden="true">✨</span> Ask AI</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>
                <form onSubmit={submit} className="modal-body ai-modal-body">
                    <div className="ai-search-row">
                        <select
                            className="ai-search-scope"
                            value={scope}
                            onChange={(e) => setScope(e.target.value)}
                            disabled={loading}
                            aria-label="Repository scope"
                        >
                            <option value="">All my repos</option>
                            {(allowedRepos ?? []).map((r) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                        <input
                            ref={inputRef}
                            type="text"
                            className="ai-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="What does the auth handshake look like?"
                            disabled={loading}
                        />
                        <button type="submit" className="btn btn--primary" disabled={loading || !q.trim()}>
                            {loading ? 'Searching…' : 'Ask'}
                        </button>
                    </div>

                    {error && <div className="form-error">{error}</div>}

                    {result?.answer && (
                        <div className="ai-answer">
                            <h3>Answer</h3>
                            <p>{result.answer}</p>
                        </div>
                    )}

                    {result && result.hits.length === 0 && !error && (
                        <p className="form-hint">
                            No matches in this scope. {scope ? 'Try expanding to all repos.' : 'Try a different phrasing.'}
                            {' '}If you just enabled AI, you may need to <strong>Re-index</strong> the repo so embeddings are populated.
                        </p>
                    )}

                    {result && result.hits.length > 0 && (
                        <>
                            <h3 className="ai-hits-heading">Sources</h3>
                            <ul className="ai-hits">
                                {result.hits.map((h) => (
                                    <li key={`${h.repoId}::${h.path}`} className={!h.openable ? 'ai-hit--restricted' : ''}>
                                        <button
                                            type="button"
                                            className="ai-hit__link"
                                            disabled={!h.openable}
                                            onClick={() => {
                                                onOpenDoc(h.repoId, h.path);
                                                onClose();
                                            }}
                                        >
                                            <span className="ai-hit__title">{h.title ?? h.path}</span>
                                            <span className="ai-hit__path">{h.repoId} · {h.path}</span>
                                        </button>
                                        <p className="ai-hit__snippet">{h.snippet}</p>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
}
